import toast from "react-hot-toast";
import { alphaNumericRegex, TRAVELER_FORM_KEYS } from "../../../constant/common";
import { isValidPhoneForCountry, getPhoneErrorMessage } from "../../../utils/phoneUtils/dialCodeToIso";
import {
  travelerDatePartsToNumber,
  travelerDatePartsToTime,
  dateToYyyymmdd,
  isValidMandatoryName,
  isValidOptionalName,
  isValidMiddleName,
  isValidNameInput,
  isValidEmail,
  getEmailLengthError,
  isValidTravelerDate,
  getMandatoryNameErrorMessage,
  getMiddleNameErrorMessage,
  getNameLengthError,
  validateTravelerDob,
  getAgeReferenceDate,
} from "../../../utils/booking/travelerForm/validateForm";
import { isLeadEmailOk } from "../../../utils/booking/travelerForm/leadEmailVerification";
import {
  shouldEnforceWebbedGuestNameRules,
  getWebbedGuestNameError,
  isDuplicateWebbedGuestName,
  DUPLICATE_GUEST_NAME_ERROR,
} from "../../../utils/booking/hotel/webbedGuestNameValidation";
import { consoleLog } from "../../../utils/consoleLog/consoleLog";
import { message } from "../../../constant";

export function updateRoomInfoForTraveler(uniqueTravelerList: any, travelerList: any) {
  try {
    let tempList = uniqueTravelerList.map((ele: any, index: number) => {
      let tempEle = ele;
      travelerList.forEach((eleTravelerList: any, indexTravelerList: number) => {
        if (tempEle?.firstName + tempEle?.lastName === eleTravelerList?.firstName + eleTravelerList?.lastName)
          tempEle = {
            ...tempEle,
            roomInfo: [...tempEle?.roomInfo, indexTravelerList + 1],
          };
      });
      return tempEle;
    });
    return tempList;
  } catch (err: any) {
    return uniqueTravelerList;
  }
}

export const PAX_TYPE = {
  adult: "ADT",
  child: "CHD",
  infant: "INF",
};

export const PASSENGER_TYPE = {
  [PAX_TYPE.adult]: "Adult",
  [PAX_TYPE.child]: "Child",
  [PAX_TYPE.infant]: "Infant",
};

export function getTravelerTypesArray(
  seniorsCount: number,
  adultsCount: number,
  childrenCount: number,
  infantCount: number,
  infantLapCount: number
): string[] {
  const result: string[] = [];

  const totalAdults = seniorsCount + adultsCount;
  result.push(...Array(totalAdults).fill(PAX_TYPE.adult));
  result.push(...Array(childrenCount).fill(PAX_TYPE.child));
  result.push(...Array(infantCount).fill(PAX_TYPE.infant));
  result.push(...Array(infantLapCount).fill(PAX_TYPE.infant));
  return result;
}

function parseFlexibleDate(dateStr: string): Date | null {
  let date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }

  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const [year, monthName, day] = parts;
    const monthNumber = new Date(`${monthName} 1, 2000`).getMonth() + 1;
    if (!isNaN(monthNumber)) {
      const mm = String(monthNumber).padStart(2, "0");
      const dd = String(day).padStart(2, "0");
      date = new Date(`${year}-${mm}-${dd}`);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
}

export function isPassportValidForSixMonths(
  travelDateStr: string,
  passportExpiryStr: string
): boolean {
  const travelDate = parseFlexibleDate(travelDateStr);
  const passportExpiryDate = parseFlexibleDate(passportExpiryStr);

  if (!travelDate || !passportExpiryDate) {
    console.error("Invalid date parsing", { travelDateStr, passportExpiryStr });
    return false;
  }

  consoleLog(["PASS", travelDate, passportExpiryDate, passportExpiryStr])
  const sixMonthsFromTravel = new Date(travelDate);
  sixMonthsFromTravel.setMonth(sixMonthsFromTravel.getMonth() + 6);

  return passportExpiryDate > sixMonthsFromTravel;
}

export function getPassengerTypeFromYearOfBirth(
  dateOfBirth: string,
  travelingDate: string,
  isFlightBooking: boolean = true
): string {
  const birthDate = parseFlexibleDate(dateOfBirth);
  const travelDate = parseFlexibleDate(travelingDate);

  if (!birthDate || !travelDate) {
    console.error("Invalid date parsing", { dateOfBirth, travelingDate });
    return PAX_TYPE.adult;
  }

  const ageInMs = travelDate.getTime() - birthDate.getTime();
  if (ageInMs < 1000 * 60 * 60 * 24) {
    return "invalid";
  }

  let ageYears = travelDate.getFullYear() - birthDate.getFullYear();

  const hasBirthdayPassed =
    travelDate.getMonth() > birthDate.getMonth() ||
    (travelDate.getMonth() === birthDate.getMonth() && travelDate.getDate() >= birthDate.getDate());

  if (!hasBirthdayPassed) {
    ageYears -= 1;
  }

  if (isFlightBooking) {
    if (ageYears < 2) return PAX_TYPE.infant;
    if (ageYears < 12) return PAX_TYPE.child;
    return PAX_TYPE.adult;
  } else {
    if (ageYears < 18) return PAX_TYPE.child;
    return PAX_TYPE.adult;
  }
}

export function removeDuplicateTravelers(travelers: any[]) {
  const seen = new Set();

  return travelers.filter((traveler) => {
    const key = `${traveler.firstName.toLowerCase()}_${traveler.lastName.toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export const scrollToDataId = (
  dataId: string,
  scrollContainerRef: React.RefObject<HTMLElement>,
  offset?: number,
) => {
  const container = scrollContainerRef?.current;
  if (!container) return;

  const target = container.querySelector(`[data-id="${dataId}"]`) as HTMLElement;
  if (!target) return;

  const containerTop = container.getBoundingClientRect().top;
  const targetTop = target.getBoundingClientRect().top;

  const scrollOffset = targetTop - containerTop + container.scrollTop - 100;

  container.scrollTo({
    top: scrollOffset,
    behavior: "smooth",
  });
};

export const validateTravelerForm = (
  travelerInfoArrayDataRef: any,
  setShowErrorMessageFlag: any,
  selectedFlightOptions: any,
  flightAvailabilityData: any,
  countriesData: any,
  childrenCount: any,
  setShouldShowErrorMessageBelowContinue: any,
  scrollContainerRef?: any,
  currentTravelerIndex?: any,
  travelToDate?: any,
  ageTravelDate?: any,
  appConfig?: any
) => {
  let scrollOfset = 0
  if (selectedFlightOptions?.flights && selectedFlightOptions?.hotels) {
    scrollOfset = 400
  } else if (!selectedFlightOptions?.flights && selectedFlightOptions?.hotels) {
    scrollOfset = 170
  }
  for (let i = 0; i < travelerInfoArrayDataRef?.length; i++) {
    const ele = travelerInfoArrayDataRef[i];

    if (!isValidMandatoryName(ele?.firstName) || !isValidNameInput(ele?.firstName)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.FIRST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getMandatoryNameErrorMessage(ele?.firstName, "first"),
        key: TRAVELER_FORM_KEYS?.FIRST_NAME
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!isValidMandatoryName(ele?.lastName) || !isValidNameInput(ele?.lastName)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.LAST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getMandatoryNameErrorMessage(ele?.lastName, "last"),
        key: TRAVELER_FORM_KEYS?.LAST_NAME
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!isValidMiddleName(ele?.middleName) || !isValidNameInput(ele?.middleName)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.MIDDLE_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 410))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getMiddleNameErrorMessage(ele?.middleName),
        key: TRAVELER_FORM_KEYS?.MIDDLE_NAME
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (shouldEnforceWebbedGuestNameRules(appConfig, selectedFlightOptions)) {
      const webbedFirstErr = getWebbedGuestNameError(ele?.firstName, "first");
      if (webbedFirstErr) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.FIRST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: webbedFirstErr, key: TRAVELER_FORM_KEYS?.FIRST_NAME });
        setShowErrorMessageFlag(true);
        return false;
      }
      const webbedLastErr = getWebbedGuestNameError(ele?.lastName, "last");
      if (webbedLastErr) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.LAST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: webbedLastErr, key: TRAVELER_FORM_KEYS?.LAST_NAME });
        setShowErrorMessageFlag(true);
        return false;
      }
      const webbedMiddleErr = getWebbedGuestNameError(ele?.middleName, "middle");
      if (webbedMiddleErr) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.MIDDLE_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 410))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: webbedMiddleErr, key: TRAVELER_FORM_KEYS?.MIDDLE_NAME });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (isDuplicateWebbedGuestName(travelerInfoArrayDataRef, i)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.LAST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: DUPLICATE_GUEST_NAME_ERROR, key: TRAVELER_FORM_KEYS?.LAST_NAME });
        setShowErrorMessageFlag(true);
        return false;
      }
    }
    const nameLenErr = getNameLengthError(ele);
    if (nameLenErr) {
      toast.dismiss();
      const nameLenKey =
        nameLenErr.field === "first"
          ? TRAVELER_FORM_KEYS?.FIRST_NAME
          : nameLenErr.field === "middle"
            ? TRAVELER_FORM_KEYS?.MIDDLE_NAME
            : TRAVELER_FORM_KEYS?.LAST_NAME;
      scrollToDataId(nameLenKey + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420));
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: nameLenErr.message,
        key: nameLenKey
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (ele?.docIssueMonth?.trim() && ele?.docIssueDate?.trim() && ele?.docIssueYear?.trim()) {
      const issueNum = travelerDatePartsToNumber(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate);
      const dobNum = travelerDatePartsToNumber(ele?.dateOfYear, ele?.dateOfBirthMonth, ele?.dateOfBirth);
      if (Number.isFinite(issueNum) && Number.isFinite(dobNum) && issueNum < dobNum) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650));
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docExpiryMonth?.trim() && ele?.docExpiryDate?.trim() && ele?.docExpiryYear?.trim()) {
        const expiryNum = travelerDatePartsToNumber(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate);
        if (Number.isFinite(expiryNum) && Number.isFinite(issueNum) && expiryNum < issueNum) {
          toast.dismiss();
          scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720));
          setShouldShowErrorMessageBelowContinue({
            flag: true,
            message: "",
            key: ""
          });
          setShowErrorMessageFlag(true);
          return false;
        }
      }
    }
    if (selectedFlightOptions.flights) {
      const dobErr = validateTravelerDob(
        ele?.dateOfBirthMonth,
        ele?.dateOfBirth,
        ele?.dateOfYear,
        ele?.paxType,
        true,
        ageTravelDate ?? travelToDate
      );
      if (dobErr) {
        toast.dismiss();
        const dobScrollKey =
          dobErr.field === "month"
            ? TRAVELER_FORM_KEYS?.DATE_OF_BIRTH_MONTH
            : dobErr.field === "year"
              ? TRAVELER_FORM_KEYS?.DATE_OF_YEAR
              : TRAVELER_FORM_KEYS?.DATE_OF_BIRTH;
        scrollToDataId(dobScrollKey + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 850));
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: dobErr.message,
          key: TRAVELER_FORM_KEYS?.DATE_OF_BIRTH
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.gender?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.GENDER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 950))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the gender`,
          key: TRAVELER_FORM_KEYS?.GENDER
        });
        setShowErrorMessageFlag(true);
        return false;
      }
    }
    if (ele?.email?.trim() !== "" && getEmailLengthError(ele?.email)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getEmailLengthError(ele?.email),
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (ele?.email?.trim() === "" && i === 0 && !currentTravelerIndex) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `Enter the email address`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!isValidEmail(ele?.email) && i === 0 && !currentTravelerIndex) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `email address is not valid`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (i === 0 && !currentTravelerIndex && !isLeadEmailOk(ele?.email)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `Please verify your email address`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return false;
    }
    if (ele?.paxType !== PAX_TYPE.child && ele?.paxType !== PAX_TYPE.infant &&
      ele?.email?.trim() !== "" && i !== 0 && !isValidEmail(ele?.email)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `email address is not valid`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      return false;
    }
    const isLeadTraveler = i === 0 && !currentTravelerIndex;
    if (
      ele?.paxType !== PAX_TYPE.child && ele?.paxType !== PAX_TYPE.infant &&
      (
        (isLeadTraveler && !isValidPhoneForCountry(ele?.phoneNumber, ele?.countryCode, countriesData)) ||
        (!isLeadTraveler && ele?.phoneNumber?.trim() !== "" && !isValidPhoneForCountry(ele?.phoneNumber, ele?.countryCode, countriesData)) ||
        String(ele?.phoneNumber?.trim()) === "0000000000"
      )
    ) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.PHONE_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1250))
      const phoneMsg = getPhoneErrorMessage(ele?.phoneNumber, ele?.countryCode, countriesData);
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: phoneMsg || `Phone number is invalid for the selected country`,
        key: TRAVELER_FORM_KEYS?.PHONE_NUMBER
      });
      setShowErrorMessageFlag(true);
      return false;
    }

    if (selectedFlightOptions?.flights && flightAvailabilityData?.isDocsMandatory) {
      const selectedIssuing = countriesData.find((item: any) => item.name === ele?.issuingCountry)?.iso;

      if (ele?.issuingCountry?.trim() === "" || !selectedIssuing) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.ISSUING_COUNTRY + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1350))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Select the Issuing country`,
          key: TRAVELER_FORM_KEYS?.ISSUING_COUNTRY
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.documentNumber?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1480))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document number`,
          key: TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (travelerInfoArrayDataRef?.filter((item: any) => item.documentNumber === ele?.documentNumber)?.length > 1) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1480))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered the document number is same as other traveller's document number. "Document Number for travellers to be unique`,
          key: TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (!alphaNumericRegex.test(ele?.documentNumber)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1480))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the valid document number`,
          key: TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER
        });
        setShowErrorMessageFlag(true);
        return false;
      }

      const selectedNationality = countriesData.find((item: any) => item.name === ele?.nationality)?.iso;
      if (ele?.nationality?.trim() === "" || !selectedNationality) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.NATIONALITY + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1580))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Select the Nationality`,
          key: TRAVELER_FORM_KEYS?.NATIONALITY
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docIssueMonth?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "Enter document issue date",
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docIssueDate?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document issue date`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docIssueYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document issue date`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (Number(ele?.docIssueDate) < 1) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document issue date is not valid`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docIssueYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document issue year`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docExpiryMonth?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "Enter document expiry date",
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docExpiryDate?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter document expiry date`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docExpiryYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter document expiry date`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (Number(ele?.docExpiryDate) < 1) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry date is not valid`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (ele?.docExpiryYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document expiry year`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (Number(ele?.docExpiryYear) < Number(ele?.docIssueYear)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry year and issue year are wrong. Expiry date should always be greater than issue year`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (
        travelerDatePartsToNumber(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate) >
        dateToYyyymmdd(new Date())
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (
        travelerDatePartsToNumber(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate) <
        travelerDatePartsToNumber(ele?.dateOfYear, ele?.dateOfBirthMonth, ele?.dateOfBirth)
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document issue year and date of birth are not valid. Issue date cannot be before the birth year`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (
        isValidTravelerDate(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate) &&
        travelerDatePartsToNumber(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate) < dateToYyyymmdd(new Date())
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Passport has expired`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (
        dateToYyyymmdd(travelToDate ? new Date(travelToDate) : new Date()) >=
        travelerDatePartsToNumber(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate)
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry should be greater than the ${travelToDate ? "travel" : "present"} date`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (
        (travelerDatePartsToTime(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate) -
          travelerDatePartsToTime(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate)) /
        (1000 * 60 * 60 * 24) <
        180
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry should be greater than the issue date by atleast 6 months`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (!isValidTravelerDate(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return false;
      }
      if (!isValidTravelerDate(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return false;
      }

    }
  }

  setShowErrorMessageFlag(false);
  return true;
};

export function isAbove17(birthYear: number) {
  if (!birthYear) return true;
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  return age >= 17;
}

export function formatUSDNumber(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function checkIfTravelerExistsInFlight(name: string, travelerInfoArrayDataRef: any, selectedFlightOptions: any) {
  if (!selectedFlightOptions?.flights) return true;
  if (!name.trim() || !travelerInfoArrayDataRef) return false;
  let ifExist = false;
  travelerInfoArrayDataRef.forEach((ele: any) => {
    if (ele?.firstName + ele?.lastName === name) ifExist = true;
  });
  return ifExist;
}

export function isNotValidPaxType(
  option: any,
  currentTravelerInfoArrayDataRef: any,
  setShouldShowErrorMessageBelowContinue: any,
  selectedFlightOptions: any,
  bookingDetails: any
) {
  const curDOB =
    currentTravelerInfoArrayDataRef.dateOfYear +
    "-" +
    currentTravelerInfoArrayDataRef.dateOfBirthMonth +
    "-" +
    currentTravelerInfoArrayDataRef.dateOfBirth;
  const optionDOB = option.dateOfYear + "-" + option.dateOfBirthMonth + "-" + option.dateOfBirth;
  const travellingDate = selectedFlightOptions?.flights
    ? getAgeReferenceDate(bookingDetails)
    : bookingDetails?.hotels?.[0]?.checkIn;
  if (option?.paxType) {
    const travelerPaxType = currentTravelerInfoArrayDataRef?.paxType
      ? currentTravelerInfoArrayDataRef?.paxType
      : getPassengerTypeFromYearOfBirth(curDOB, travellingDate, !!selectedFlightOptions?.flights);
    if (option?.paxType !== travelerPaxType) {
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `Traveler should be ${PASSENGER_TYPE[
          currentTravelerInfoArrayDataRef?.paxType
            ? currentTravelerInfoArrayDataRef?.paxType
            : getPassengerTypeFromYearOfBirth(curDOB, travellingDate, !!selectedFlightOptions?.flights)
        ]
          }`,
        key: TRAVELER_FORM_KEYS?.DATE_OF_BIRTH
      });
      return true;
    }
  } else if (option?.dateOfYear !== "") {
    const travelerPaxType = currentTravelerInfoArrayDataRef?.paxType
      ? currentTravelerInfoArrayDataRef?.paxType
      : getPassengerTypeFromYearOfBirth(curDOB, travellingDate, !!selectedFlightOptions?.flights);
    if (getPassengerTypeFromYearOfBirth(optionDOB, travellingDate, !!selectedFlightOptions?.flights) !== travelerPaxType) {
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `Traveler should be ${PASSENGER_TYPE[
          currentTravelerInfoArrayDataRef?.paxType
            ? currentTravelerInfoArrayDataRef?.paxType
            : getPassengerTypeFromYearOfBirth(curDOB, travellingDate, !!selectedFlightOptions?.flights)
        ]
          }`,
        key: TRAVELER_FORM_KEYS?.DATE_OF_BIRTH
      });
      return true;
    }
  } else {
    return false;
  }
}

export function validateFlightTravelerForm(
  travelerForm: any,
  selectedFlightOptions: any,
  flightAvailabilityData: any,
  setShowErrorMessageFlag: any,
  index: number,
  ageTravelDate?: any
) {
  if (!travelerForm?.firstName?.trim() || !isValidOptionalName(travelerForm?.firstName)) {
    setShowErrorMessageFlag(true);
    return false;
  }

  if (!travelerForm?.lastName?.trim() || !isValidOptionalName(travelerForm?.lastName)) {
    setShowErrorMessageFlag(true);
    return false;
  }

  if (!isValidOptionalName(travelerForm?.middleName)) {
    setShowErrorMessageFlag(true);
    return false;
  }

  if (!travelerForm?.gender?.trim() && selectedFlightOptions?.flights) {
    setShowErrorMessageFlag(true);
    return false;
  }

  if (
    selectedFlightOptions?.flights &&
    validateTravelerDob(
      travelerForm?.dateOfBirthMonth,
      travelerForm?.dateOfBirth,
      travelerForm?.dateOfYear,
      travelerForm?.paxType,
      true,
      ageTravelDate
    )
  ) {
    setShowErrorMessageFlag(true);
    return false;
  }

  if (index === 0) {
    if (!travelerForm?.email?.trim()) {
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!isValidEmail(travelerForm?.email)) {
      setShowErrorMessageFlag(true);
      return false;
    }
  }

  if (travelerForm?.paxType !== PAX_TYPE.child && travelerForm?.paxType !== PAX_TYPE.infant) {
    if (!isValidPhoneForCountry(travelerForm?.phoneNumber, travelerForm?.countryCode)) {
      setShowErrorMessageFlag(true);
      return false;
    }
  }

  if (selectedFlightOptions?.flights && flightAvailabilityData?.isDocsMandatory) {
    if (!travelerForm?.issuingCountry?.trim()) {
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!travelerForm?.nationality?.trim()) {
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!travelerForm?.documentNumber?.trim()) {
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!travelerForm?.docIssueDate?.trim() || !travelerForm?.docIssueMonth?.trim() || !travelerForm?.docIssueYear?.trim()) {
      setShowErrorMessageFlag(true);
      return false;
    }
    if (!travelerForm?.docExpiryDate?.trim() || !travelerForm?.docExpiryMonth?.trim() || !travelerForm?.docExpiryYear?.trim()) {
      setShowErrorMessageFlag(true);
      return false;
    }
  }
  return true;
}

export const validateTravelerFormAndReturnTheObject = (
  travelerInfoArrayDataRef: any,
  setShowErrorMessageFlag: any,
  selectedFlightOptions: any,
  flightAvailabilityData: any,
  countriesData: any,
  childrenCount: any,
  setShouldShowErrorMessageBelowContinue: any,
  scrollContainerRef?: any,
  currentTravelerIndex?: any,
  travelToDate?: any,
  ageTravelDate?: any,
  appConfig?: any
) => {
  let scrollOfset = 0
  if (selectedFlightOptions?.flights && selectedFlightOptions?.hotels) {
    scrollOfset = 400
  } else if (!selectedFlightOptions?.flights && selectedFlightOptions?.hotels) {
    scrollOfset = 170
  }
  for (let i = 0; i < travelerInfoArrayDataRef?.length; i++) {
    const ele = travelerInfoArrayDataRef[i];

    if (!isValidMandatoryName(ele?.firstName) || !isValidNameInput(ele?.firstName)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.FIRST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getMandatoryNameErrorMessage(ele?.firstName, "first"),
        key: TRAVELER_FORM_KEYS?.FIRST_NAME
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (!isValidMandatoryName(ele?.lastName) || !isValidNameInput(ele?.lastName)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.LAST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getMandatoryNameErrorMessage(ele?.lastName, "last"),
        key: TRAVELER_FORM_KEYS?.LAST_NAME
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (!isValidMiddleName(ele?.middleName) || !isValidNameInput(ele?.middleName)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.MIDDLE_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 410))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getMiddleNameErrorMessage(ele?.middleName),
        key: TRAVELER_FORM_KEYS?.MIDDLE_NAME
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (shouldEnforceWebbedGuestNameRules(appConfig, selectedFlightOptions)) {
      const webbedFirstErr = getWebbedGuestNameError(ele?.firstName, "first");
      if (webbedFirstErr) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.FIRST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: webbedFirstErr, key: TRAVELER_FORM_KEYS?.FIRST_NAME });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      const webbedLastErr = getWebbedGuestNameError(ele?.lastName, "last");
      if (webbedLastErr) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.LAST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: webbedLastErr, key: TRAVELER_FORM_KEYS?.LAST_NAME });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      const webbedMiddleErr = getWebbedGuestNameError(ele?.middleName, "middle");
      if (webbedMiddleErr) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.MIDDLE_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 410))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: webbedMiddleErr, key: TRAVELER_FORM_KEYS?.MIDDLE_NAME });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (isDuplicateWebbedGuestName(travelerInfoArrayDataRef, i)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.LAST_NAME + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420))
        setShouldShowErrorMessageBelowContinue({ flag: true, message: DUPLICATE_GUEST_NAME_ERROR, key: TRAVELER_FORM_KEYS?.LAST_NAME });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
    }
    const nameLenErr = getNameLengthError(ele);
    if (nameLenErr) {
      toast.dismiss();
      const nameLenKey =
        nameLenErr.field === "first"
          ? TRAVELER_FORM_KEYS?.FIRST_NAME
          : nameLenErr.field === "middle"
            ? TRAVELER_FORM_KEYS?.MIDDLE_NAME
            : TRAVELER_FORM_KEYS?.LAST_NAME;
      scrollToDataId(nameLenKey + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 420));
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: nameLenErr.message,
        key: nameLenKey
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (ele?.docIssueMonth?.trim() && ele?.docIssueDate?.trim() && ele?.docIssueYear?.trim()) {
      const issueNum = travelerDatePartsToNumber(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate);
      const dobNum = travelerDatePartsToNumber(ele?.dateOfYear, ele?.dateOfBirthMonth, ele?.dateOfBirth);
      if (Number.isFinite(issueNum) && Number.isFinite(dobNum) && issueNum < dobNum) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650));
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docExpiryMonth?.trim() && ele?.docExpiryDate?.trim() && ele?.docExpiryYear?.trim()) {
        const expiryNum = travelerDatePartsToNumber(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate);
        if (Number.isFinite(expiryNum) && Number.isFinite(issueNum) && expiryNum < issueNum) {
          toast.dismiss();
          scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720));
          setShouldShowErrorMessageBelowContinue({
            flag: true,
            message: "",
            key: ""
          });
          setShowErrorMessageFlag(true);
          return { index: i, data: ele };
        }
      }
    }
    if (selectedFlightOptions.flights) {
      const dobErr = validateTravelerDob(
        ele?.dateOfBirthMonth,
        ele?.dateOfBirth,
        ele?.dateOfYear,
        ele?.paxType,
        true,
        ageTravelDate ?? travelToDate
      );
      if (dobErr) {
        toast.dismiss();
        const dobScrollKey =
          dobErr.field === "month"
            ? TRAVELER_FORM_KEYS?.DATE_OF_BIRTH_MONTH
            : dobErr.field === "year"
              ? TRAVELER_FORM_KEYS?.DATE_OF_YEAR
              : TRAVELER_FORM_KEYS?.DATE_OF_BIRTH;
        scrollToDataId(dobScrollKey + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 850));
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: dobErr.message,
          key: TRAVELER_FORM_KEYS?.DATE_OF_BIRTH
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.gender?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.GENDER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 950))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the gender`,
          key: TRAVELER_FORM_KEYS?.GENDER
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
    }
    if (ele?.email?.trim() !== "" && getEmailLengthError(ele?.email)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: getEmailLengthError(ele?.email),
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (ele?.email?.trim() === "" && i === 0 && !currentTravelerIndex) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `Enter the email address`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (!isValidEmail(ele?.email) && i === 0 && !currentTravelerIndex) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `email address is not valid`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (i === 0 && !currentTravelerIndex && !isLeadEmailOk(ele?.email)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `Please verify your email address`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }
    if (ele?.paxType !== PAX_TYPE.child && ele?.paxType !== PAX_TYPE.infant &&
      ele?.email?.trim() !== "" && i !== 0 && !isValidEmail(ele?.email)) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.EMAIL + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 400))
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: `email address is not valid`,
        key: TRAVELER_FORM_KEYS?.EMAIL
      });
      return { index: i, data: ele };
    }
    const isLeadTraveler = i === 0 && !currentTravelerIndex;
    if (
      ele?.paxType !== PAX_TYPE.child && ele?.paxType !== PAX_TYPE.infant &&
      (
        (isLeadTraveler && !isValidPhoneForCountry(ele?.phoneNumber, ele?.countryCode, countriesData)) ||
        (!isLeadTraveler && ele?.phoneNumber?.trim() !== "" && !isValidPhoneForCountry(ele?.phoneNumber, ele?.countryCode, countriesData)) ||
        String(ele?.phoneNumber?.trim()) === "0000000000"
      )
    ) {
      toast.dismiss();
      scrollToDataId(TRAVELER_FORM_KEYS?.PHONE_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1250))
      const phoneMsg = getPhoneErrorMessage(ele?.phoneNumber, ele?.countryCode, countriesData);
      setShouldShowErrorMessageBelowContinue({
        flag: true,
        message: phoneMsg || `Phone number is invalid for the selected country`,
        key: TRAVELER_FORM_KEYS?.PHONE_NUMBER
      });
      setShowErrorMessageFlag(true);
      return { index: i, data: ele };
    }

    if (selectedFlightOptions?.flights && flightAvailabilityData?.isDocsMandatory) {
      const selectedIssuing = countriesData.find((item: any) => item.name === ele?.issuingCountry)?.iso;

      if (ele?.issuingCountry?.trim() === "" || !selectedIssuing) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.ISSUING_COUNTRY + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1350))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Select the Issuing country`,
          key: TRAVELER_FORM_KEYS?.ISSUING_COUNTRY
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.documentNumber?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1480))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document number`,
          key: TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (travelerInfoArrayDataRef?.filter((item: any) => item.documentNumber === ele?.documentNumber)?.length > 1) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1480))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered the document number is same as other traveller's document number. "Document Number for travellers to be unique`,
          key: TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (!alphaNumericRegex.test(ele?.documentNumber)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1480))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the valid document number`,
          key: TRAVELER_FORM_KEYS?.DOCUMENT_NUMBER
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }

      const selectedNationality = countriesData.find((item: any) => item.name === ele?.nationality)?.iso;
      if (ele?.nationality?.trim() === "" || !selectedNationality) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.NATIONALITY + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1580))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Select the Nationality`,
          key: TRAVELER_FORM_KEYS?.NATIONALITY
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docIssueMonth?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "Enter document issue date",
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docIssueDate?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document issue date`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docIssueYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document issue date`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (Number(ele?.docIssueDate) < 1) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document issue date is not valid`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docIssueYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document issue year`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docExpiryMonth?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "Enter document expiry date",
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docExpiryDate?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter document expiry date`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docExpiryYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter document expiry date`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (Number(ele?.docExpiryDate) < 1) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry date is not valid`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (ele?.docExpiryYear?.trim() === "") {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Enter the document expiry year`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (Number(ele?.docExpiryYear) < Number(ele?.docIssueYear)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry year and issue year are wrong. Expiry date should always be greater than issue year`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (
        travelerDatePartsToNumber(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate) >
        dateToYyyymmdd(new Date())
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (
        travelerDatePartsToNumber(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate) <
        travelerDatePartsToNumber(ele?.dateOfYear, ele?.dateOfBirthMonth, ele?.dateOfBirth)
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document issue year and date of birth are not valid. Issue date cannot be before the birth year`,
          key: TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (
        isValidTravelerDate(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate) &&
        travelerDatePartsToNumber(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate) < dateToYyyymmdd(new Date())
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Passport has expired`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (
        dateToYyyymmdd(travelToDate ? new Date(travelToDate) : new Date()) >=
        travelerDatePartsToNumber(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate)
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry should be greater than the ${travelToDate ? "travel" : "present"} date`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (
        (travelerDatePartsToTime(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate) -
          travelerDatePartsToTime(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate)) /
        (1000 * 60 * 60 * 24) <
        180
      ) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: `Entered document expiry should be greater than the issue date by atleast 6 months`,
          key: TRAVELER_FORM_KEYS?.DOC_EXPIRY_DATE
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (!isValidTravelerDate(ele?.docIssueYear, ele?.docIssueMonth, ele?.docIssueDate)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_ISSUE_DATE + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1650))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }
      if (!isValidTravelerDate(ele?.docExpiryYear, ele?.docExpiryMonth, ele?.docExpiryDate)) {
        toast.dismiss();
        scrollToDataId(TRAVELER_FORM_KEYS?.DOC_EXPIRY_YEAR + (currentTravelerIndex ?? i), scrollContainerRef, -(scrollOfset + 1720))
        setShouldShowErrorMessageBelowContinue({
          flag: true,
          message: "",
          key: ""
        });
        setShowErrorMessageFlag(true);
        return { index: i, data: ele };
      }

    }
  }

  setShowErrorMessageFlag(false);
  return { index: travelerInfoArrayDataRef?.length - 1, data: travelerInfoArrayDataRef.at(-1) };
};